import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { getActiveCoupons, getCouponByCode, createCoupon, updateCoupon, deleteCoupon } from '../firebase/firestore';

const CouponContext = createContext();

// Available coupon codes with their details
const AVAILABLE_COUPONS = {
  'WELCOME10': {
    code: 'WELCOME10',
    discount: 10,
    type: 'percentage',
    description: '10% off on your first order',
    minAmount: 1000,
    maxDiscount: 500
  },
  'SAVE20': {
    code: 'SAVE20',
    discount: 20,
    type: 'percentage',
    description: '20% off on orders above ₹5000',
    minAmount: 5000,
    maxDiscount: 2000
  },
  'FLAT500': {
    code: 'FLAT500',
    discount: 500,
    type: 'fixed',
    description: 'Flat ₹500 off on orders above ₹3000',
    minAmount: 3000
  },
  'BUYONE50': {
    code: 'BUYONE50',
    discount: 50,
    type: 'percentage',
    description: '50% off on your second item (Buy One Get One)',
    minAmount: 2000,
    maxDiscount: 1000
  }
};

export const CouponProvider = ({ children }) => {
  const getCouponCache = () => {
    if (typeof window === 'undefined') {
      return { data: null, isValid: false };
    }
    try {
      const cached = localStorage.getItem('admin_coupons_cache');
      if (cached) {
        const parsed = JSON.parse(cached);
        if (parsed.timestamp && (Date.now() - parsed.timestamp < 5 * 60 * 1000)) {
          return { data: parsed.data, isValid: true };
        }
      }
    } catch (e) {
      // Ignore cache errors
    }
    return { data: null, isValid: false };
  };

  const cachedCoupons = getCouponCache();
  const cachedCouponsRef = React.useRef(cachedCoupons.isValid);
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponError, setCouponError] = useState('');
  const [availableCoupons, setAvailableCoupons] = useState(cachedCoupons.data || {});
  const [loading, setLoading] = useState(!cachedCoupons.isValid);
  const [error, setError] = useState(null);

  // Load coupons from Firestore on component mount
  useEffect(() => {
    loadCoupons();
  }, []);

  const loadCoupons = async () => {
    try {
      if (!cachedCouponsRef.current) {
        setLoading(true);
      }
      setError(null);
      const coupons = await getActiveCoupons();
      setAvailableCoupons(coupons);
      cachedCouponsRef.current = true;
      try {
        localStorage.setItem('admin_coupons_cache', JSON.stringify({
          data: coupons,
          timestamp: Date.now()
        }));
      } catch (e) {
        // Ignore cache errors
      }
    } catch (err) {
      console.error('❌ Error loading coupons:', err);
      setError(err.message);
      // Fallback to default coupons if Firestore fails
      setAvailableCoupons(AVAILABLE_COUPONS);
      cachedCouponsRef.current = true;
      try {
        localStorage.setItem('admin_coupons_cache', JSON.stringify({
          data: AVAILABLE_COUPONS,
          timestamp: Date.now()
        }));
      } catch (e) {
        // Ignore cache errors
      }
    } finally {
      setLoading(false);
    }
  };

  const applyCoupon = useCallback(async (couponCode, cartTotal, cartItems) => {
    setCouponError('');

    try {
      // First check local state, then fetch from Firestore if not found
      let coupon = availableCoupons[couponCode.toUpperCase()];

      if (!coupon) {
        // Try to fetch from Firestore
        coupon = await getCouponByCode(couponCode);
        if (!coupon) {
          setCouponError('Invalid coupon code');
          return false;
        }
      }

      if (cartTotal < coupon.minAmount) {
        setCouponError(`Minimum order amount of ₹${coupon.minAmount} required for this coupon`);
        return false;
      }

      // Special logic for BUYONE50 (Buy One Get One)
      if (couponCode.toUpperCase() === 'BUYONE50' && cartItems.length < 2) {
        setCouponError('Add at least 2 items to use this coupon');
        return false;
      }

      setAppliedCoupon(coupon);
      return true;
    } catch (error) {
      console.error('Error applying coupon:', error);
      setCouponError('Error validating coupon. Please try again.');
      return false;
    }
  }, [availableCoupons]);

  const removeCoupon = useCallback(() => {
    setAppliedCoupon(null);
    setCouponError('');
  }, []);

  const calculateDiscount = useCallback((cartTotal) => {
    if (!appliedCoupon) return 0;

    let discount = 0;

    if (appliedCoupon.type === 'percentage') {
      discount = (cartTotal * appliedCoupon.discount) / 100;
      if (appliedCoupon.maxDiscount) {
        discount = Math.min(discount, appliedCoupon.maxDiscount);
      }
    } else if (appliedCoupon.type === 'fixed') {
      discount = appliedCoupon.discount;
    }

    return Math.round(discount);
  }, [appliedCoupon]);

  const getFinalTotal = useCallback((cartTotal) => {
    const discount = calculateDiscount(cartTotal);
    return Math.max(0, cartTotal - discount);
  }, [calculateDiscount]);

  // Admin functions for coupon management
  const addCoupon = useCallback(async (couponData) => {
    try {
      const couponId = await createCoupon(couponData);
      // Reload coupons to get the latest data
      await loadCoupons();
      return couponId;
    } catch (error) {
      console.error('Error adding coupon:', error);
      throw error;
    }
  }, []);

  const updateCouponById = useCallback(async (couponId, updateData) => {
    try {
      await updateCoupon(couponId, updateData);
      // Reload coupons to get the latest data
      await loadCoupons();
    } catch (error) {
      console.error('Error updating coupon:', error);
      throw error;
    }
  }, []);

  const deleteCouponById = useCallback(async (couponId) => {
    try {
      await deleteCoupon(couponId);
      // Reload coupons to get the latest data
      await loadCoupons();
    } catch (error) {
      console.error('Error deleting coupon:', error);
      throw error;
    }
  }, []);

  const refreshCoupons = useCallback(async () => {
    await loadCoupons();
  }, []);

  const value = {
    appliedCoupon,
    couponError,
    applyCoupon,
    removeCoupon,
    calculateDiscount,
    getFinalTotal,
    availableCoupons,
    loading,
    error,
    addCoupon,
    updateCoupon: updateCouponById,
    deleteCoupon: deleteCouponById,
    refreshCoupons
  };

  return (
    <CouponContext.Provider value={value}>
      {children}
    </CouponContext.Provider>
  );
};

export const useCoupon = () => {
  const context = useContext(CouponContext);
  if (!context) {
    throw new Error('useCoupon must be used within a CouponProvider');
  }
  return context;
};
